import React, { useState } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    TextField,
    FormHelperText,
    Button,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useTranslate, useInput } from 'react-admin';
import { Translate, ContentCopy, SwapHoriz } from '@mui/icons-material';
import * as OpenCC from 'opencc-js';

interface MessageMultiLanguageFieldsProps {
    formData?: any;
}

export const MessageMultiLanguageFields: React.FC<MessageMultiLanguageFieldsProps> = ({
    formData
}) => {
    const [currentLang, setCurrentLang] = useState(0);
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translationsInput = useInput({ source: 'translations' });

    const languages = [
        { code: 'en', name: 'English' },
        { code: 'zh-TW', name: '繁體中文' },
        { code: 'zh-CN', name: '简体中文' }
    ];

    const fields = [
        {
            name: 'title',
            label: translate('resources.messages.fields.title'),
            required: true,
            helperText: translate('resources.messages.multilang.titleHelperText'),
            multiline: false
        },
        {
            name: 'content',
            label: translate('resources.messages.fields.content'),
            required: true,
            helperText: translate('resources.messages.multilang.contentHelperText'),
            multiline: true,
            rows: 4
        }
    ];

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentLang(newValue);
    };

    // Get translations from form data
    const translations = React.useMemo(() =>
        translationsInput.field.value || [],
        [translationsInput.field.value]
    );

    const currentTranslation = React.useMemo(() =>
        translations[currentLang] || {},
        [translations, currentLang]
    );

    // Initialize translations array if empty
    React.useEffect(() => {
        if (translations.length === 0) {
            const initialTranslations = languages.map(lang => ({
                language: lang.code,
                title: '',
                content: ''
            }));
            translationsInput.field.onChange(initialTranslations);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const handleFieldChange = (fieldName: string, value: string) => {
        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }
        updatedTranslations[currentLang][fieldName] = value;
        translationsInput.field.onChange(updatedTranslations);
    };

    const handleCopyFromBasic = () => {
        if (!formData) return;

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Copy values from basic information for each field
        fields.forEach(field => {
            if (formData[field.name] !== undefined) {
                updatedTranslations[currentLang][field.name] = formData[field.name] || '';
            }
        });

        // Update translations in a single call
        translationsInput.field.onChange(updatedTranslations);
    };

    const handleTranslateFromSimplified = () => {
        // Find the simplified Chinese translation (zh-CN)
        const simplifiedIndex = languages.findIndex(lang => lang.code === 'zh-CN');
        const simplifiedTranslation = translations[simplifiedIndex];

        if (!simplifiedTranslation) {
            return;
        }

        // Create converter from Simplified to Traditional
        const converter = OpenCC.Converter({ from: 'cn', to: 'tw' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from simplified to traditional
        fields.forEach(field => {
            const simplifiedValue = simplifiedTranslation[field.name];
            if (simplifiedValue && typeof simplifiedValue === 'string') {
                updatedTranslations[currentLang][field.name] = converter(simplifiedValue);
            }
        });

        translationsInput.field.onChange(updatedTranslations);
    };

    const handleTranslateFromTraditional = () => {
        // Find the traditional Chinese translation (zh-TW)
        const traditionalIndex = languages.findIndex(lang => lang.code === 'zh-TW');
        const traditionalTranslation = translations[traditionalIndex];

        if (!traditionalTranslation) {
            return;
        }

        // Create converter from Traditional to Simplified
        const converter = OpenCC.Converter({ from: 'tw', to: 'cn' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from traditional to simplified
        fields.forEach(field => {
            const traditionalValue = traditionalTranslation[field.name];
            if (traditionalValue && typeof traditionalValue === 'string') {
                updatedTranslations[currentLang][field.name] = converter(traditionalValue);
            }
        });

        translationsInput.field.onChange(updatedTranslations);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Translate sx={{ mr: 1, color: '#00C853' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {translate('resources.messages.multilang.title')}
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.messages.multilang.helperText')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: 1 }}>
                <Tabs
                    value={currentLang}
                    onChange={handleTabChange}
                    variant={isMobile ? "fullWidth" : "standard"}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: { xs: 0, sm: 90 },
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }
                    }}
                >
                    {languages.map((lang) => (
                        <Tab key={lang.code} label={lang.name} />
                    ))}
                </Tabs>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyFromBasic}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#00C853',
                            color: '#00C853',
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                            px: { xs: 1, sm: 2 },
                            '&:hover': {
                                borderColor: '#00A843',
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            }
                        }}
                    >
                        {translate('resources.messages.multilang.copyFromBasic')}
                    </Button>

                    {/* Show translation button for Traditional Chinese tab */}
                    {languages[currentLang].code === 'zh-TW' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromSimplified}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                px: { xs: 1, sm: 2 },
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            {translate('resources.messages.multilang.translateFromSimplified')}
                        </Button>
                    )}

                    {/* Show translation button for Simplified Chinese tab */}
                    {languages[currentLang].code === 'zh-CN' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromTraditional}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                px: { xs: 1, sm: 2 },
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            {translate('resources.messages.multilang.translateFromTraditional')}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {fields.map((field) => (
                    <Box key={field.name}>
                        <TextField
                            fullWidth
                            label={field.label}
                            value={currentTranslation[field.name] || ''}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            multiline={field.multiline}
                            rows={field.rows}
                            required={field.required && languages[currentLang].code === 'en'}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#FAFAFA',
                                    '&:hover': {
                                        backgroundColor: '#F5F5F5',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#FFFFFF',
                                    },
                                },
                            }}
                        />
                        {field.helperText && (
                            <FormHelperText>{field.helperText}</FormHelperText>
                        )}
                    </Box>
                ))}
            </Box>

            {languages[currentLang].code !== 'en' && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#E8F5E9',
                    borderRadius: 1,
                    border: '1px solid #C8E6C9'
                }}>
                    <Typography variant="caption" color="text.secondary">
                        {translate('resources.messages.multilang.nonEnglishNote')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};